import { createSelector } from "reselect";

import products from "./products";

const services = {};

/**
 * Returns all services.
 * @param {Object} state
 * @returns {Object[]} services
 */
services.get = state => state.services.items;

/**
 * Returns uri of current service.
 * @param {Object} state
 * @returns {string} uri
 */
services.getCurrentUri = state => state.services.current;

services.getMatched = (state, props) =>
  state.services.items.find(
    service => props.match.params.name === service.name
  );

/**
 * Returns filter term for services.
 * @param {Object} state
 * @returns {string} filterTerm
 */
services.getFilterTerm = state => state.services.filterTerm;

/**
 * Returns self-referential 'RBAC' service.
 * @param {Object} state
 * @returns {Object[]} services
 */
services.getRbac = createSelector(
  [services.get],
  services =>
    services.find(service => service.$uri === "/api/rbac/v1/service/0")
);

/**
 * Returns all services except self-referential 'RBAC' service.
 * @param {Object} state
 * @returns {Object[]} services
 */
services.getFiltered = createSelector(
  [services.get],
  services =>
    services.filter(service => service.$uri !== "/api/rbac/v1/service/0")
);

/**
 * Returns current service object.
 * @param {Object} state
 * @returns {Object} service
 */
services.getCurrent = createSelector(
  [services.getCurrentUri, services.get],
  (currentServiceUri, services) => {
    if (!currentServiceUri) {
      return undefined;
    }
    return services.find(service => service.$uri === currentServiceUri);
  }
);

/**
 * Returns if services errors exist.
 * @param {Object} state
 * @returns {boolean}
 */
services.hasErrors = state =>
  Object.prototype.hasOwnProperty.call(state.services, "error") || false;

/**
 * Returns an error object for services.
 * @returns {(Object|null)} An object with an error and description or null.
 */
services.getError = state => {
  if (state.services.error) {
    return {
      description: state.services.errorDescription,
      error: state.services.error
    };
  }
  return null;
};

/**
 * Returns services filtered by services.filterTerm.
 * @returns {Object[]} services
 */
services.getByFilter = createSelector(
  [services.getFiltered, services.getFilterTerm],
  (services, filter) => {
    if (filter) {
      return services.filter(service =>
        service.name.toLowerCase().includes(filter)
      );
    }
    return services;
  }
);

/**
 * Returns current service hydrated with product details.
 * @returns {Object} service
 */
services.getCurrentWithDetails = createSelector(
  [services.getCurrent, products.get],
  (currentService, products) => {
    let hydratedService;

    if (currentService) {
      hydratedService = { ...currentService };
      hydratedService.product = products.find(
        product => currentService.product.$ref === product.$uri
      );
    }

    return hydratedService;
  }
);

export default services;
